import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { Middleware } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import { createMigrate } from 'redux-persist';
import { persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import services from '../services/index';
import { migrations } from './migrations';
import authReducer from './reducers/AuthSlice';
import testReducer from './reducers/TodosSlice';
import dataGridColumnVisibilityModelReducer from './reducers/dataGridColumnVisibilityModel/dataGridColumnVisibilityModelSlice';
import dataGridColumnWidthsReducer from './reducers/dataGridColumnWidths/dataGridColumnWidths';
import dataGridOrderReducer from './reducers/dataGridOrder/DataGridOrderSlice';
import dataGridRowsForPageReducer from './reducers/dataGridRowsForPage/dataGridRowsForPage';
import uiReducer from './reducers/uiSlice';

//defaults to localStorage for web

const {
  postAPI,
  userAPI,
  pointAPI,
  ticketAPI,
  companyAPI,
  clientAPI,
  warehouseAPI,
  serviceCatalogAPI,
  refreshTokenAPI,
  onbordingAPI,
} = services;

const reducers = {
  testReducer,
  authReducer,
  uiReducer,
  dataGridOrderReducer,
  dataGridColumnVisibilityModelReducer,
  dataGridColumnWidthsReducer,
  dataGridRowsForPageReducer,
  [postAPI.reducerPath]: postAPI.reducer,
  [userAPI.reducerPath]: userAPI.reducer,
  [pointAPI.reducerPath]: pointAPI.reducer,
  [ticketAPI.reducerPath]: ticketAPI.reducer,
  [companyAPI.reducerPath]: companyAPI.reducer,
  [clientAPI.reducerPath]: clientAPI.reducer,
  [warehouseAPI.reducerPath]: warehouseAPI.reducer,
  [serviceCatalogAPI.reducerPath]: serviceCatalogAPI.reducer,
  [refreshTokenAPI.reducerPath]: refreshTokenAPI.reducer,
  [onbordingAPI.reducerPath]: onbordingAPI.reducer,
};

const appReducer = combineReducers(reducers);

const rootReducer = (state: any, action: any) => {
  if (
    userAPI.endpoints.logout.matchFulfilled(action) ||
    action.type === 'RESET_APP_STATE'
  ) {
    state = undefined;
  }
  return appReducer(state, action);
};

const persistConfig = {
  key: 'root',
  storage,
  version: 14,
  whitelist: [
    'testReducer',
    'authReducer',
    'uiReducer',
    'dataGridOrderReducer',
    'dataGridColumnVisibilityModelReducer',
    'dataGridColumnWidthsReducer',
    'dataGridRowsForPageReducer',
  ],
  migrate: createMigrate(migrations, { debug: false }),
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

// Custom middleware for logging
const loggingMiddleware: Middleware = (store) => (next) => (action: any) => {
  const result = next(action);

  if (action.type === 'auth/logOut') {
    console.log('Persisted state cleared due to logout', store);
  }

  return result;
};

export const setupStore = () => {
  return configureStore({
    //reducer: rootReducer,
    reducer: persistedReducer,
    //preloadedState: initialState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false, // avoid errors with non-serializable
      })
        .concat(loggingMiddleware)
        .concat(postAPI.middleware)
        .concat(userAPI.middleware)
        .concat(pointAPI.middleware)
        .concat(ticketAPI.middleware)
        .concat(companyAPI.middleware)
        .concat(clientAPI.middleware)
        .concat(warehouseAPI.middleware)
        .concat(serviceCatalogAPI.middleware)
        .concat(refreshTokenAPI.middleware)
        .concat(onbordingAPI.middleware),
    devTools: true, // Enable Redux DevTools
  });
};

export const store = setupStore();
export const persistor = persistStore(store);

export type RootState = ReturnType<typeof rootReducer>;
export type AppStore = ReturnType<typeof setupStore>;
export type AppDispatch = AppStore['dispatch'];
